import React, { useEffect, useState } from 'react';
import client from '../../../api/client';
import Toggle from '../../../components/Toggle';

function AgentLocationModal({ agent, assignments, locations, onClose, onSave }) {
  const assigned = new Set(assignments.filter(a => a.user === agent.id).map(a => a.location));
  const [selected, setSelected] = useState(new Set(assigned));
  const [saving, setSaving] = useState(false);

  const toggle = (locId) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(locId) ? next.delete(locId) : next.add(locId);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const toAdd = [...selected].filter(id => !assigned.has(id));
      const toRemove = assignments.filter(a => a.user === agent.id && !selected.has(a.location));
      await Promise.all([
        ...toAdd.map(locId => client.post('/config/agent-assignments/add/', { user: agent.id, location: locId })),
        ...toRemove.map(a => client.delete(`/config/agent-assignments/${a.id}/`)),
      ]);
      onSave();
      onClose();
    } catch {}
    setSaving(false);
  };

  const initials = (agent.full_name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-brand-50">
          <div className="w-10 h-10 rounded-full bg-brand-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">{initials}</div>
          <div>
            <p className="font-semibold text-gray-800">{agent.full_name}</p>
            <p className="text-xs text-gray-400">{agent.email} · {agent.role}</p>
          </div>
          <button onClick={onClose} className="ml-auto text-gray-300 hover:text-gray-500">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 flex-1 overflow-y-auto">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Select Locations</p>
          <div className="space-y-1.5">
            {locations.map(loc => {
              const checked = selected.has(loc.id);
              return (
                <label key={loc.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-colors ${checked ? 'bg-brand-50 border border-brand-200' : 'border border-transparent hover:bg-gray-50'}`}>
                  <div className={`w-4 h-4 rounded flex items-center justify-center border-2 flex-shrink-0 transition-colors ${checked ? 'bg-brand-500 border-brand-500' : 'border-gray-300'}`}>
                    {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
                  </div>
                  <input type="checkbox" className="hidden" checked={checked} onChange={() => toggle(loc.id)} />
                  <span className="text-sm text-gray-700 font-medium">{loc.name}</span>
                  {loc.city && <span className="text-xs text-gray-400 ml-auto">{loc.city}</span>}
                </label>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-brand-50">
          <span className="text-xs text-gray-400">{selected.size} location{selected.size !== 1 ? 's' : ''} selected</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 text-sm font-semibold bg-brand-500 text-white rounded-xl hover:bg-brand-400 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TeamPermissions() {
  const [data, setData] = useState({ assignments: [], agents: [], locations: [] });
  const [permData, setPermData] = useState({ matrix: [], roles: [] });
  const [editingAgent, setEditingAgent] = useState(null);

  const load = () => client.get('/config/agent-assignments/').then(r => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    client.get('/config/role-permissions/').then(r => setPermData(r.data)).catch(() => {});
  }, []);

  const togglePerm = async (from_status, to_status, role, current) => {
    const newVal = !current;
    setPermData(p => ({
      ...p,
      matrix: p.matrix.map(row =>
        row.from_status === from_status && row.to_status === to_status
          ? { ...row, roles: { ...row.roles, [role]: newVal } }
          : row
      ),
    }));
    await client.patch('/config/role-permissions/update/', { role, from_status, to_status, is_allowed: newVal });
  };

  return (
    <div className="space-y-6">
      {editingAgent && (
        <AgentLocationModal
          agent={editingAgent}
          assignments={data.assignments}
          locations={data.locations}
          onClose={() => setEditingAgent(null)}
          onSave={load}
        />
      )}

      {/* Agent Location Assignments */}
      <div className="card p-6 space-y-4">
        <div>
          <h2 className="font-bold text-brand-500 text-sm">Agent Location Assignments</h2>
          <p className="text-xs text-gray-400 mt-0.5">Click an agent to assign or remove locations</p>
        </div>

        <div className="space-y-2">
          {data.agents.map(agent => {
            const agentAssignments = data.assignments.filter(a => a.user === agent.id);
            const initials = (agent.full_name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
            return (
              <button
                key={agent.id}
                onClick={() => setEditingAgent(agent)}
                className="w-full flex items-center gap-4 px-4 py-3 rounded-xl bg-brand-50/50 border border-brand-100 hover:border-brand-300 hover:bg-brand-50 transition-all text-left group"
              >
                <div className="w-9 h-9 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{initials}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800">{agent.full_name}</p>
                  <p className="text-xs text-gray-400">{agent.email} · {agent.role}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {agentAssignments.length === 0 ? (
                      <span className="text-xs text-gray-300">No locations assigned</span>
                    ) : agentAssignments.map(a => (
                      <span key={a.id} className="inline-flex items-center text-xs bg-white border border-brand-200 text-brand-600 font-medium px-2 py-0.5 rounded-full">
                        {a.location_name}
                      </span>
                    ))}
                  </div>
                </div>
                <svg className="w-4 h-4 text-gray-300 group-hover:text-brand-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
              </button>
            );
          })}
          {data.agents.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No agents found</p>
          )}
        </div>
      </div>

      {/* Role Permissions Matrix */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-brand-50">
          <h2 className="font-bold text-brand-500 text-sm">Role Transition Permissions</h2>
          <p className="text-xs text-gray-400 mt-0.5">Control which roles can move tickets through each status transition</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'linear-gradient(to right, #F5F3FC, #EAE7F8)' }}>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Transition</th>
                {(permData.roles || []).map(role => (
                  <th key={role} className="px-4 py-3 text-center text-xs font-bold text-brand-400 uppercase tracking-wider">
                    {role.replace('_', ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-50">
              {(permData.matrix || []).map(row => (
                <tr key={`${row.from_status}-${row.to_status}`} className="hover:bg-brand-50/30">
                  <td className="px-4 py-2.5">
                    <span className="text-xs font-medium text-gray-600">{row.from_status}</span>
                    <span className="text-gray-300 mx-1.5">→</span>
                    <span className="text-xs font-medium text-brand-600">{row.to_status}</span>
                  </td>
                  {(permData.roles || []).map(role => (
                    <td key={role} className="px-4 py-2.5 text-center">
                      {role === 'ADMIN' ? (
                        <span className="text-emerald-500 text-lg" title="Admin always has access">✓</span>
                      ) : (
                        <Toggle size="sm" enabled={!!row.roles[role]} onToggle={() => togglePerm(row.from_status, row.to_status, role, row.roles[role])} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
